class SQuickAddModal extends HTMLElement {
  #ac;
  #fetchController;
  #dialog;
  #content;
  #status;
  #triggerElement;
  #productUrl = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#content = this.querySelector('[data-quick-add-content]');
    this.#status = this.querySelector('[data-quick-add-status]');

    if (!this.#dialog || !this.#content) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--open-product') this.#openProduct(event.source);
    }, opts);

    // Finishes are linked products: choosing one inside quick view reloads the popup with that
    // product instead of navigating (capture phase, so the page-level finish switcher never sees it).
    this.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[data-finish-link]');
      if (!link || !this.contains(link) || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const url = new URL(link.href, window.location.origin);
      this.#productUrl = url.pathname;
      this.#loadProduct(url.pathname, { keepContent: true });
    }, { ...opts, capture: true });

    this.addEventListener('change', (event) => {
      this.#handleVariantChange(event);
    }, opts);

    // Stay open while the add is in flight so s-product-form's loading state and any inline error
    // stay visible; close only once the item is in the cart and the cart drawer opens.
    document.addEventListener('s-cart:opened', () => {
      if (!this.#dialog?.open) return;
      this.#triggerElement = null; // focus belongs to the cart drawer now
      this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      this.#fetchController?.abort();
      this.removeAttribute('aria-busy');
      this.#restoreFocus();
    }, opts);

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
  }

  async #openProduct(source) {
    if (!(source instanceof HTMLElement)) return;

    const productUrl = source.dataset.productUrl;
    if (!productUrl) return;

    this.#triggerElement = source;
    this.#productUrl = productUrl;
    this.#setLoading();
    this.#showDialog();

    await this.#loadProduct(productUrl);
  }

  async #handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('[data-quick-add-variant-picker]');
    if (!picker || !this.contains(picker)) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || !this.#productUrl) return;

    this.#syncVariantInput(variantId);

    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    await this.#loadProduct(url.toString(), { focusVariantId: variantId, keepContent: true });
  }

  async #loadProduct(productUrl, { focusVariantId = null, keepContent = false } = {}) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;

    this.setAttribute('aria-busy', 'true');
    if (!keepContent) this.#setLoading();

    try {
      const response = await fetch(this.#themeUrl(productUrl), { signal });
      if (!response.ok) throw new Error(`Failed to load quick add product: ${response.status}`);

      const responseText = await response.text();
      signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const quickAddSource = html.querySelector('[data-quick-add-source]');
      if (!quickAddSource) throw new Error('Quick add source was not found in product response.');

      const content = this.#prepareProductContent(quickAddSource);
      signal.throwIfAborted();

      this.#content.replaceChildren(content);
      this.#setupGallery(content);
      this.#initializePaymentButtons();
      this.#announce('');
      this.removeAttribute('aria-busy');

      if (focusVariantId) this.#focusVariant(focusVariantId);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[s-quick-add-modal] Product fetch failed:', error);
      this.removeAttribute('aria-busy');
      this.#setError();
    } finally {
      if (this.#fetchController?.signal === signal) this.#fetchController = null;
    }
  }

  /**
   * Fetch the product from the same theme the visitor is viewing. In theme previews and the editor,
   * a background request without preview_theme_id can be answered by the live theme, whose page has
   * no [data-quick-add-source], so quick view showed an error. The live (main) theme needs no parameter.
   */
  #themeUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    const theme = window.Shopify?.theme;
    const pagePreviewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    const previewId = pagePreviewId || (theme && theme.role !== 'main' ? theme.id : null);
    if (previewId && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', previewId);
    return url.toString();
  }

  #prepareProductContent(source) {
    const content = source.cloneNode(true);

    content.className = 'flex flex-col gap-5 p-6 pt-14 md:p-10 md:pt-12';
    content.removeAttribute('data-quick-add-source');

    for (const script of content.querySelectorAll('script')) script.remove();
    content.querySelector('#inventory-status')?.remove();

    for (const picker of content.querySelectorAll('s-variant-picker')) {
      const replacement = document.createElement('div');
      for (const attribute of picker.attributes) {
        if (attribute.name === 'class') continue;
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.className = picker.className;
      replacement.setAttribute('data-quick-add-variant-picker', '');
      while (picker.firstChild) replacement.appendChild(picker.firstChild);
      picker.replaceWith(replacement);
    }

    // Quick view layout: drop product-page-only parts, reveal popup-only parts, and put the
    // [data-quick-view-media] image beside the buy area using the layout classes from Liquid.
    for (const element of content.querySelectorAll('[data-quick-view-hide]')) element.remove();
    for (const element of content.querySelectorAll('[data-quick-view-only]')) element.hidden = false;
    const media = content.querySelector('[data-quick-view-media]');
    const layout = source.dataset.quickViewLayout;
    if (media && layout) {
      media.remove();
      media.hidden = false;
      const wrapper = document.createElement('div');
      wrapper.className = layout;
      wrapper.append(media, content);
      return wrapper;
    }

    return content;
  }

  // Quick view gallery: dots scroll the image track, and swiping updates the active dot.
  #setupGallery(root) {
    const track = root.querySelector('[data-qv-track]');
    const dots = [...root.querySelectorAll('[data-qv-dot]')];
    if (!track || dots.length === 0) return;
    const prev = root.querySelector('[data-qv-prev]');
    const next = root.querySelector('[data-qv-next]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = 0;
    const setActive = (index) => {
      current = index;
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === dots.length - 1;
    };
    const goTo = (i) => {
      if (!track.children[i]) return;
      const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      track.scrollTo({ left: i * track.clientWidth * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
      setActive(i);
    };
    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
    prev?.addEventListener('click', () => goTo(Math.max(0, current - 1)));
    next?.addEventListener('click', () => goTo(Math.min(dots.length - 1, current + 1)));
    let frame = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = track.clientWidth || 1;
        setActive(Math.min(dots.length - 1, Math.round(Math.abs(track.scrollLeft) / width)));
      });
    }, { passive: true });
    setActive(0);
  }

  #initializePaymentButtons() {
    if (!this.#content?.querySelector('.shopify-payment-button, [data-shopify-payment-button], shopify-accelerated-checkout')) return;

    window.Shopify?.PaymentButton?.init?.();
  }

  #showDialog() {
    if (!this.#dialog?.open) this.#dialog.showModal();
  }

  #setLoading() {
    const loadingText = this.dataset.loadingText || '';
    this.#announce(loadingText);
    this.#content?.replaceChildren(this.#skeletonNode());
  }

  /** Text-free placeholder: two equal columns (image | details) with a soft shimmer, mirroring the quick view layout. */
  #skeletonNode() {
    const template = document.createElement('template');
    template.innerHTML = `<div class="grid md:grid-cols-2" aria-hidden="true">
      <div class="skeleton-block aspect-square w-full rounded-none"></div>
      <div class="flex flex-col gap-4 p-6 md:p-10">
        <div class="skeleton-block h-7 w-3/4"></div>
        <div class="skeleton-block h-4 w-1/3"></div>
        <div class="mt-4 flex gap-2"><div class="skeleton-block h-10 flex-1"></div><div class="skeleton-block h-10 flex-1"></div><div class="skeleton-block h-10 flex-1"></div></div>
        <div class="flex gap-2"><div class="skeleton-block h-10 flex-1"></div><div class="skeleton-block h-10 flex-1"></div></div>
        <div class="skeleton-block mt-4 h-12 w-full"></div>
        <div class="skeleton-block h-3 w-full"></div>
        <div class="skeleton-block h-3 w-5/6"></div>
      </div>
    </div>`;
    return template.content.firstElementChild;
  }

  #setError() {
    const errorMessage = this.dataset.errorMessage || '';
    this.#announce(errorMessage);
    this.#content?.replaceChildren(this.#messageNode(errorMessage));
  }

  #messageNode(message) {
    const node = document.createElement('p');
    node.className = 'py-12 text-center text-sm text-on-surface-secondary';
    node.textContent = message;
    return node;
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #syncVariantInput(variantId) {
    const form = this.#content?.querySelector('form');
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  #focusVariant(variantId) {
    requestAnimationFrame(() => {
      for (const input of this.#content?.querySelectorAll('[data-variant-id]') || []) {
        if (input instanceof HTMLElement && input.dataset.variantId === variantId) {
          input.focus();
          return;
        }
      }
    });
  }

  #restoreFocus() {
    if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }
}

if (!customElements.get('s-quick-add-modal')) {
  customElements.define('s-quick-add-modal', SQuickAddModal);
}
